import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { normalizePageSlug, isReservedPageSlug } from "@profolio/validators";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Globe, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import type { PageFormData } from "./types";

interface CreatePageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingSlugs: string[];
  subdomain?: string;
  onSuccess?: () => void;
}

export function CreatePageModal({
  open,
  onOpenChange,
  existingSlugs,
  subdomain,
  onSuccess,
}: CreatePageModalProps) {
  const [formData, setFormData] = useState<PageFormData>({
    title: "",
    navigationLabel: "",
    slug: "",
    description: "",
    visible: true,
    seoTitle: "",
    seoDescription: "",
  });

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);
  const [showSeo, setShowSeo] = useState(false);

  const utils = trpc.useUtils();

  const createPageMutation = trpc.pages.createPage.useMutation({
    onSuccess: () => {
      toast.success(`Page "${formData.title}" created successfully!`);
      utils.pages.listPages.invalidate();
      utils.profile.getMyProfile.invalidate();
      onOpenChange(false);
      onSuccess?.();
      resetForm();
    },
    onError: (err: { message?: string }) => {
      setServerError(err.message || "Failed to create page.");
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      navigationLabel: "",
      slug: "",
      description: "",
      visible: true,
      seoTitle: "",
      seoDescription: "",
    });
    setSlugManuallyEdited(false);
    setSlugError(null);
    setServerError(null);
    setShowSeo(false);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Live validation of slug
  const validateSlug = (slugToValidate: string): string | null => {
    const normalized = normalizePageSlug(slugToValidate);
    if (!normalized || normalized.trim() === "") {
      return "URL slug cannot be empty.";
    }
    if (isReservedPageSlug(normalized)) {
      return `The slug '${normalized}' is reserved by the platform.`;
    }
    if (existingSlugs.some((s) => s.toLowerCase() === normalized.toLowerCase())) {
      return `A page with the slug '/${normalized}' already exists.`;
    }
    return null;
  };

  const handleTitleChange = (val: string) => {
    const updated: PageFormData = {
      ...formData,
      title: val,
      navigationLabel: !formData.navigationLabel || formData.navigationLabel === formData.title
        ? val
        : formData.navigationLabel,
    };

    if (!slugManuallyEdited) {
      const derivedSlug = normalizePageSlug(val);
      updated.slug = derivedSlug;
      setSlugError(val.trim() ? validateSlug(derivedSlug) : null);
    }
    setFormData(updated);
    setServerError(null);
  };

  const handleSlugChange = (rawVal: string) => {
    setSlugManuallyEdited(true);
    const normalized = normalizePageSlug(rawVal);
    setFormData((prev) => ({ ...prev, slug: normalized }));
    setSlugError(validateSlug(normalized));
    setServerError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);

    const error = validateSlug(formData.slug);
    if (error) {
      setSlugError(error);
      return;
    }

    if (!formData.title.trim()) {
      setServerError("Page title is required.");
      return;
    }

    createPageMutation.mutate({
      title: formData.title.trim(),
      navigationLabel: formData.navigationLabel.trim() || formData.title.trim(),
      slug: formData.slug,
      description: formData.description.trim() || undefined,
      visible: formData.visible,
      seoTitle: formData.seoTitle.trim() || undefined,
      seoDescription: formData.seoDescription.trim() || undefined,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Create New Page"
      description="Add a dedicated page route (e.g., Publications, Research, Lab Team) to your portfolio."
    >
      <form onSubmit={handleSubmit} className="space-y-4 pt-2">
        {serverError && (
          <div className="flex items-start gap-2.5 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{serverError}</span>
          </div>
        )}

        <div className="space-y-3.5">
          {/* Page Title */}
          <div className="space-y-1.5">
            <Label htmlFor="page-title" className="text-xs font-semibold">
              Page Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="page-title"
              value={formData.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="e.g., Research & Labs"
              autoFocus
              required
              className="h-9 text-sm"
            />
          </div>

          {/* Navigation Label */}
          <div className="space-y-1.5">
            <Label htmlFor="page-nav-label" className="text-xs font-semibold">
              Navigation Label
            </Label>
            <Input
              id="page-nav-label"
              value={formData.navigationLabel}
              onChange={(e) => setFormData({ ...formData, navigationLabel: e.target.value })}
              placeholder="e.g., Research"
              className="h-9 text-sm"
            />
            <p className="text-[11px] text-muted-foreground">
              Text displayed in the top navbar menu (defaults to page title).
            </p>
          </div>

          {/* URL Slug */}
          <div className="space-y-1.5">
            <Label htmlFor="page-slug" className="text-xs font-semibold">
              URL Slug <span className="text-destructive">*</span>
            </Label>
            <div className="flex items-center rounded-md border border-input bg-muted/40 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent px-3 h-9 text-sm">
              <Globe className="h-3.5 w-3.5 text-muted-foreground mr-1 shrink-0" />
              <span className="text-muted-foreground text-xs select-none">
                {subdomain ? `${subdomain}.profolio.lk/` : "/"}
              </span>
              <input
                id="page-slug"
                type="text"
                value={formData.slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="research"
                className="bg-transparent border-none outline-none text-foreground flex-1 text-sm font-mono lowercase"
                required
              />
            </div>
            {slugError ? (
              <p className="text-[11px] text-destructive font-medium">{slugError}</p>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                The URL path for this page. Lowercase letters, numbers, and hyphens only.
              </p>
            )}
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <Label htmlFor="page-desc" className="text-xs font-semibold">
              Page Description (Optional)
            </Label>
            <Textarea
              id="page-desc"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief summary of this page's focus or content..."
              rows={2}
              className="text-xs resize-none"
            />
          </div>

          {/* Visibility Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-slate-50 dark:bg-muted/30">
            <div className="space-y-0.5">
              <Label htmlFor="page-visible" className="text-xs font-semibold cursor-pointer">
                Visible in Navigation
              </Label>
              <p className="text-[11px] text-muted-foreground">
                When enabled, appears in header and mobile drawer.
              </p>
            </div>
            <Switch
              id="page-visible"
              checked={formData.visible}
              onCheckedChange={(checked) => setFormData({ ...formData, visible: checked })}
            />
          </div>

          {/* SEO Settings Accordion */}
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowSeo(!showSeo)}
              className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold text-muted-foreground hover:text-foreground bg-muted/20 hover:bg-muted/40 transition-colors"
            >
              <span>Search Engine & Social Media (SEO)</span>
              {showSeo ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>

            {showSeo && (
              <div className="p-3 space-y-3 bg-card border-t border-border">
                <div className="space-y-1.5">
                  <Label htmlFor="page-seo-title" className="text-xs font-medium">
                    SEO Title
                  </Label>
                  <Input
                    id="page-seo-title"
                    value={formData.seoTitle}
                    onChange={(e) => setFormData({ ...formData, seoTitle: e.target.value })}
                    placeholder={formData.title || "Page title for Google and browser tabs"}
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="page-seo-desc" className="text-xs font-medium">
                    SEO Meta Description
                  </Label>
                  <Textarea
                    id="page-seo-desc"
                    value={formData.seoDescription}
                    onChange={(e) => setFormData({ ...formData, seoDescription: e.target.value })}
                    placeholder="Snippet appearing in search engine results and social sharing cards..."
                    rows={2}
                    className="text-xs resize-none"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={createPageMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={createPageMutation.isPending || Boolean(slugError) || !formData.title.trim()}
            className="gap-1.5"
          >
            {createPageMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Create Page
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
